/**
 * Verification card component
 */

import { useState } from 'react';
import type { Verification } from '../../../../packages/shared/src/types';

interface VerificationCardProps {
  verification: Verification;
  mediaName?: string;
  onClick?: (id: string) => void;
}

export function VerificationCard({ verification, mediaName, onClick }: VerificationCardProps) {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-900/80 text-green-300';
      case 'failed':
        return 'bg-red-900/80 text-red-300';
      case 'in_progress':
        return 'bg-yellow-900/80 text-yellow-300';
      default:
        return 'bg-gray-700 text-gray-300';
    }
  };

  const getVpnColor = (status: string) => {
    switch (status) {
      case 'clean':
        return 'text-green-400';
      case 'vpn':
      case 'proxy':
        return 'text-red-400';
      case 'tor':
        return 'text-orange-400';
      default:
        return 'text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div 
      className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden hover:border-indigo-500 transition-colors cursor-pointer"
      onClick={() => onClick?.(verification.id)}
    >
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {mediaName && (
              <p className="text-sm text-indigo-400 font-medium">{mediaName}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">ID: {verification.id}</p>
          </div>
          <span className={`px-3 py-1 text-xs font-medium rounded ${getStatusColor(verification.status)}`}>
            {verification.status}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500">IP Address</p>
            <p className="text-sm text-white font-mono">{verification.ipAddress}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">VPN Status</p>
            <p className={`text-sm font-medium ${getVpnColor(verification.vpnStatus)}`}>
              {verification.vpnStatus.toUpperCase()}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Camera Permission</p>
            <p className={`text-sm ${
              verification.cameraPermission === 'granted' ? 'text-green-400' :
              verification.cameraPermission === 'denied' ? 'text-red-400' :
              'text-gray-400'
            }`}>
              {verification.cameraPermission}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Location Permission</p>
            <p className={`text-sm ${
              verification.locationPermission === 'granted' ? 'text-green-400' :
              verification.locationPermission === 'denied' ? 'text-red-400' :
              'text-gray-400'
            }`}>
              {verification.locationPermission}
            </p>
          </div>
        </div>

        {verification.location && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <p className="text-xs text-gray-500 mb-2">Location</p>
            <p className="text-sm text-white">
              {verification.location.city && `${verification.location.city}, `}
              {verification.location.region && `${verification.location.region}, `}
              {verification.location.country}
            </p>
          </div>
        )}

        {verification.videoRecordingUrl && (
          <div className="mt-4">
            <a
              href={verification.videoRecordingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View Recording
            </a>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
          <p className="text-xs text-gray-500">{formatDate(verification.createdAt)}</p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="text-xs text-indigo-400 hover:text-indigo-300"
          >
            {expanded ? 'Less' : 'More'} info
          </button>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <p className="text-xs text-gray-500 mb-1">User Agent</p>
            <p className="text-xs text-gray-400 font-mono break-all">{verification.userAgent}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerificationCard;